import { useEffect, useState } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, PackageX } from "lucide-react";

const EMPTY = { name: "", description: "", image: "", price: 0, stock: 0, active: true };

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/products", { params: { all: true } }).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(p); setOpen(true); };

  const save = async () => {
    const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Produto salvo"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/products/${id}`); toast.success("Removido"); load(); } catch { toast.error("Erro"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Produtos</h1><p className="text-zinc-400 mt-1">Estrutura pronta para sua loja futura.</p></div>
        <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={openNew} data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-500"><Package className="w-10 h-10 mx-auto mb-3 text-zinc-600" />Nenhum produto cadastrado. Adicione para preparar sua loja.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((p) => (
            <div key={p.id} className={`bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden ${p.active ? "" : "opacity-60"}`} data-testid={`product-item-${p.id}`}>
              <div className="h-36 overflow-hidden">{p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-zinc-800 flex items-center justify-center"><Package className="w-8 h-8 text-zinc-600" /></div>}</div>
              <div className="p-4">
                <h3 className="font-semibold text-white text-sm">{p.name}</h3>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-amber-400 font-bold">{money(p.price)}</span>
                  <span className={`text-xs flex items-center gap-1 ${p.stock > 0 ? "text-zinc-400" : "text-red-400"}`}>{p.stock > 0 ? <>{p.stock} un.</> : <><PackageX className="w-3 h-3" /> Sem estoque</>}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing ? "Editar" : "Novo"} Produto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-zinc-300">Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="product-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-zinc-300">Preço (R$)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="product-price" /></div>
              <div><Label className="text-zinc-300">Estoque</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="product-stock" /></div>
            </div>
            <div><Label className="text-zinc-300">Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="product-description" /></div>
            <div><Label className="text-zinc-300">URL da Foto</Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="product-image" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active" /><Label className="text-zinc-300">Disponível</Label></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-amber-400 text-black font-bold" data-testid="save-product-btn">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
